<template>
  <div class="container" role="main" data-test="my-development-view">
    <ViewpointsInviteDialog ref="viewpointsInviteDialog" data-test="md-invite-modal" />
    <div v-if="!isLoading" class="team-lead-grid">
      <div v-for="level in levels" :key="level.id" class="grid-row">
        <div class="grid-item level-circle" :class="level.status">
          <ProgressCircle
            v-if="level.type === 'level'"
            variant="user"
            :number="level.number"
            :status="level.status"
          />
        </div>
        <div
          v-if="level.type === 'milestone'"
          class="grid-item milestone"
          :class="level.status"
          data-test="my-development-milestones"
        >
          <h4 data-test="milestone-title">
            {{ cms.asText(level.data.title) }}
            <span v-if="level.status === 'completed'">
              {{ Array.from({ length: level.number }, () => "🎉").join("") }}
            </span>
          </h4>
        </div>

        <div v-else class="grid-item" data-test="my-development-levels">
          <CompletableAccordion
            :id="level.id"
            :status="level.status"
            variant="user"
            :is-open="level.isOpen"
            @accordionOpened="onAccordionOpen"
            @accordionClosed="onAccordionClose"
          >
            <template #trigger>
              <div class="level-header">
                <div class="header-text">
                  <h6 :class="level.status" data-test="level-number">Level {{ level.number }}:</h6>
                  <div
                    v-html="cms.asHtml(level.data.title)"
                    class="level-title"
                    :class="level.status"
                    data-test="level-title"
                  ></div>
                </div>
              </div>
            </template>
            <div class="level-content">
              <div v-html="cms.asHtml(castAsLevel(level.data).description)"></div>
              <h4>Uplifts</h4>
              <ul class="uplifts">
                <li
                  v-for="(uplift, index) in castAsLevel(level.data).uplifts_list"
                  :key="index"
                  v-html="cms.asHtml(uplift.uplift)"
                ></li>
              </ul>
              <h4>Practicalities</h4>
              <ul class="practicalities">
                <li
                  v-for="(practicality, index) in castAsLevel(level.data).practicalities_list"
                  :key="index"
                  v-html="cms.asHtml(practicality.practicality)"
                ></li>
              </ul>
              <div class="level-button-container">
                <a
                  v-if="castAsLevel(level.data).level_type === 'session'"
                  :href="setLevelLink(level)"
                  class="btn"
                  :class="[
                    applyDefaultClass(level) ? 'btn-default-forward' : 'btn-primary-forward',
                  ]"
                  data-test="level-go-to-session-button"
                  >{{ cms.asText(castAsLevel(level.data).go_button_text) }}</a
                >
                <!-- this button is only for the invite level and can go once that becomes a session -->
                <button
                  v-else-if="castAsLevel(level.data).level_type === 'viewpoint-invite-modal'"
                  @click="onGoClick(castAsLevel(level.data), level)"
                  class="btn btn-primary-forward"
                  type="button"
                  data-test="level-go-to-other-action-button"
                >
                  {{ cms.asText(castAsLevel(level.data).go_button_text) }}
                </button>

                <!-- complete level button -->
                <button
                  v-if="level.status !== 'completed'"
                  @click="complete(level)"
                  class="btn btn-default-tick"
                  type="button"
                  data-test="level-complete-button"
                >
                  <LoadingSpinner v-if="isSavingCompletion" size="15px" />
                  {{ isSavingCompletion ? "" : "Complete" }}
                </button>
              </div>
              <div v-if="completeErrorMessage.length" class="complete-error-message">
                <p class="text-center text-danger" data-test="level-complete-error">
                  {{ completeErrorMessage }}
                </p>
              </div>
            </div>
          </CompletableAccordion>
        </div>
      </div>

      <!-- New level Modal-->
      <CompleteLevelModal
        v-if="showLevelModal"
        variant="user"
        :levels-completed="levelsCompleted"
        :model-value="showLevelModal"
        @close-complete-level-modal="closeLevelModal"
      />

      <!-- New Milestone Modal -->
      <Modal v-if="showMilestoneModal" v-model="showMilestoneModal" :header="false" :footer="false">
        <div class="milestone-modal" data-test="milestone-modal">
          <div>
            <h2>New Milestone!</h2>
            <p>Congratulations on Milestone {{ levelOrMilestoneNumber }}. Keep up the good work!</p>
          </div>
          <div class="milestone-badge">
            <img src="../../assets/team-lead/flag-icon.svg" alt="Milestone Flag" />
            <h4>{{ levelOrMilestoneNumber }}</h4>
            <p>Milestone</p>
          </div>
          <div style="margin-bottom: 10px">
            <button
              class="btn btn-primary"
              @click="refreshPage"
              type="button"
              data-test="milestone-modal-button"
            >
              Continue
            </button>
          </div>
        </div>
      </Modal>
      <!-- End of New Milestone Modal -->
    </div>
    <LoadingPage v-else />
  </div>
</template>

<script setup lang="ts">
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  onUnmounted,
  provide,
  Ref,
  ref,
  triggerRef,
  watch,
} from "vue";
import { Modal } from "uiv";
import axios, { AxiosError } from "axios";
import { debug as d } from "debug";
import usePersonalLevels, { LevelItem } from "../../composables/usePersonalLevels";
import CompletableAccordion from "../../components/reusables/completable-accordion.vue";
import cms from "../../services/cms/cms";
import { CmsDocumentTypes } from "../../services/cms/cms-type-configuration";
import {
  InviteControlApiModel,
  PersonalCompletedLevelsAndMilestonesApiModel,
} from "../../api-models";
import LoadingPage from "../loading-page.vue";
import ProgressCircle from "../../components/reusables/progress-circle.vue";
import { useToggle } from "../../composables/useToggle";
import { Analytics } from "../../services/analytics/analytics";
import errorRedirect from "../../services/utilities/error-redirect";
import { CmsPersonalLevelData } from "../../services/cms/cms-type-definitions";
import LoadingSpinner from "../../components/loading-spinner.vue";
import CompleteLevelModal from "../../components/team-lead-area/complete-level-modal.vue";
import localStorageService from "../../services/utilities/local-storage-service";
import useAxios from "../../composables/api";
import ViewpointsInviteDialog from "../../components/invite-control/viewpoints-invite-dialog.vue";

const debug = d("cs.MyDevelopoment");

const isLoading = ref(true);
const levels = ref<LevelItem[]>([]) as Ref<LevelItem[]>;
const showMilestoneModal = ref(false);

const scrollPosition = ref(0);

const updateScroll = () => {
  scrollPosition.value = window.scrollY;
  localStorageService.save("myDevelopmentScrollPosition", scrollPosition.value);
};

const { prepareLevelsAndMilestones, setLevelsStatus, castAsLevel, setLevelLink, completeLevel } =
  usePersonalLevels();

const activeLevel = computed(() => levels.value.find((level) => level.status === "active"));
const levelOrMilestoneNumber = computed(() => activeLevel.value?.number);

const selectedLevel = ref<LevelItem>(
  levels.value.find((level) => level.status === "active") || ({} as LevelItem),
);
provide("selectedLevel", selectedLevel);

const completeErrorMessage = ref("");
const isSavingCompletion = ref(false);

const levelsCompleted = computed(() => {
  return (
    levels.value.filter((level) => level.status === "completed" && level.type === "level").length +
    1
  );
});

const viewpointsInviteDialog = ref<InstanceType<typeof ViewpointsInviteDialog> | null>(null);

const { data: inviteControlModel, promise: inviteControlModelPromise } =
  useAxios<InviteControlApiModel>("/InviteCodes/ViewpointsReviewInviteModel");

function showViewpointsInviteDialog(): void {
  if (inviteControlModel.value) {
    viewpointsInviteDialog.value?.open(inviteControlModel.value).finally(() => {
      viewpointsInviteDialog.value?.close();
    });
  }
}

function openViewpointsInviteDialog(): void {
  debug("openViewpointsInviteDialog");
  if (inviteControlModel.value) {
    debug("showing immediately");
    showViewpointsInviteDialog();
  } else {
    debug("deferring show");
    inviteControlModelPromise?.then(() => {
      debug("showing after deferral");
      showViewpointsInviteDialog();
    });
  }
}

function onGoClick(levelData: CmsPersonalLevelData, level: LevelItem) {
  if (levelData.level_type === "session") {
    return;
  }
  // can get rid of this button once the invite level becomes a session
  // TODO: handle the viewpoint invites modal
  if (levelData.level_type === "viewpoint-invite-modal") {
    selectedLevel.value = level;
    openViewpointsInviteDialog();
  }
}

async function getPersonalLevelsProgress() {
  const response = await axios.get<PersonalCompletedLevelsAndMilestonesApiModel>(
    "/api/personallevels",
  );

  return response.data;
}

function onAccordionClose(id: string) {
  const index = levels.value.findIndex((level) => level.id === id);
  if (index !== -1) {
    levels.value[index] = { ...levels.value[index], isOpen: false };
    triggerRef(levels);
  }
  localStorageService.save("myDevelopmentOpenLevelId", null);
}

function onAccordionOpen(id: string) {
  const openLevel = levels.value.find((level) => level.id === id);
  localStorageService.save("myDevelopmentOpenLevelId", id);
  levels.value = levels.value.map((level) => ({
    ...level,
    isOpen: level.id === id,
  }));
  Analytics.action("Expanded Level Content", "MyDevelopmentActions", {
    levelTitle: cms.asText(openLevel?.data.title),
    levelNumber: openLevel?.number,
    levelStatus: openLevel?.status,
  });
}

function refreshPage() {
  window.location.reload();
}

const { state: showLevelModal, toggleState: toggleShowLevelModal } = useToggle(false);

function applyDefaultClass(level: LevelItem): boolean {
  //   const parsed = castAsLevel(level.data);
  //   if (!parsed.initiate_pathway) {
  return false;
  //   }

  //   return displayCompleteButton(level);
}

function closeLevelModal() {
  toggleShowLevelModal();
  refreshPage();
}

async function complete(level: LevelItem) {
  if (isSavingCompletion.value) {
    return;
  }

  isSavingCompletion.value = true;
  const { status, errorMessage } = await completeLevel(level);
  if (status.value === "error") {
    completeErrorMessage.value = errorMessage.value;
    isSavingCompletion.value = false;
  }

  if (status.value === "success") {
    isSavingCompletion.value = false;
    toggleShowLevelModal();
  }
}

async function getLevelsData() {
  const response = await cms.getByUid(
    CmsDocumentTypes.CmsPersonalLevelsAndMilestones,
    "personal-levels-master-list",
  );
  return prepareLevelsAndMilestones(response.data.personal_levels_and_milestones_list);
}

onMounted(async () => {
  window.addEventListener("scroll", updateScroll);

  try {
    const [levelsResponse, progressResponse] = await Promise.all([
      getLevelsData(),
      getPersonalLevelsProgress(),
    ]);
    levels.value = levelsResponse;
    if (progressResponse) {
      setLevelsStatus(levels.value, progressResponse);
    }
  } catch (e: unknown) {
    const error = e as AxiosError;
    debug(error.message);
    errorRedirect(error.response?.status || 500);
  } finally {
    isLoading.value = false;
    nextTick(() => {
      window.scrollTo(0, localStorageService.get("myDevelopmentScrollPosition") || 0);
    });
  }
});

async function completeMilestone() {
  try {
    await axios.post("/api/personallevels/CompleteMilestone", {
      title: cms.asText(activeLevel.value?.data.title),
      prismicId: activeLevel.value?.id,
      prismicUid: activeLevel.value?.uid,
      milestoneNumber: activeLevel.value?.number,
    });
  } catch (e) {
    const error = e as AxiosError;
    debug(error.message);
  }
}

watch(levels, async () => {
  if (activeLevel.value?.type === "milestone") {
    await completeMilestone();
    showMilestoneModal.value = true;
  }
});

onBeforeUnmount(() => {
  window.removeEventListener("scroll", updateScroll);
});

onUnmounted(() => {
  localStorageService.save("myDevelopmentScrollPosition", scrollPosition.value);
});
</script>
<style lang="less" scoped>
@import (reference) "../../styles/bootstrap/variables.less";

@accordion-collapsed-height: 64px;
@progress-circle-size: 30px;

.team-lead-grid {
  display: grid;
  grid-template-columns: auto 1fr;
  row-gap: 10px;
  column-gap: 40px;
  margin: 20px auto 60px;
  max-width: 80%;
}

.grid-row {
  display: contents;
}

.grid-item {
  display: flex;
  justify-content: center;
}

.grid-item:first-child {
  grid-column: 1;
  align-items: start;
  margin-top: calc((@accordion-collapsed-height - @progress-circle-size) / 2);
}

.active.grid-item:first-child {
  margin: 0;
}

.grid-item:last-child {
  grid-column: 2;
  width: 100%;
  align-items: center;
}

.header-text {
  h6 {
    color: @form-text-color;
    margin-bottom: 0;
    padding-left: 1ch;
    font-size: 14px;
  }

  h6.active {
    color: #ffffff;
  }
}

.level-content {
  font-size: 16px;
  padding: 10px 40px;

  @media screen and (max-width: @screen-xxs-max) {
    padding: 10px 20px;
  }

  h4 {
    color: #fcaf17;
    margin-top: 30px;
  }

  ul {
    list-style-type: none;

    li {
      p {
        color: @form-text-color;
      }
    }
  }

  @padding-left: 30px;

  ul.uplifts {
    padding-left: @padding-left;

    li {
      background-image: url("../../assets/team-lead/star-icon.svg");
      background-repeat: no-repeat;
      background-position: left center;
      padding-left: @padding-left;
    }
  }

  ul.practicalities {
    // to align the bullet points with the uplifts
    padding-left: calc(@padding-left + 5px);

    li {
      background-image: url("../../assets/team-lead/bullet-point.svg");
      background-repeat: no-repeat;
      background-position: left center;
      padding-left: calc(@padding-left - 5px);
    }
  }
}

.level-circle {
  @media screen and (max-width: @screen-xxs-max) {
    display: none;
  }
}

.level-button-container {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;

  a.btn,
  button.btn {
    min-width: 120px;
  }
}

.level-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-grow: 1;
  gap: 20px;

  @media (max-width: @screen-xxs-max) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0;
  }
}

.complete-error-message {
  margin-top: 10px;
}

.grid-item.milestone {
  width: 80%;
}

.milestone {
  grid-column: 2;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  margin: auto;
  padding: 5px;
  border-radius: 10px;
  background-color: transparent;
  // filter: opacity(0.5);

  h4 {
    color: #dddddd;
  }
}

.completed.milestone {
  background-color: transparent;
  position: relative;
  filter: opacity(1);
  border: 1px solid #eeeeee;
  padding: 20px 0;
  margin-top: 10px;
  margin-bottom: 10px;

  h4 {
    color: @off-black;
    font-size: 26px;
  }
}

.milestone-modal {
  display: flex;
  text-align: center;
  gap: 20px;
  align-items: center;
  flex-direction: column;

  p {
    font-size: 16px;
    margin-top: -5px;
    color: @form-text-color;
  }

  button {
    //font-weight: bold;
    padding: 6px 24px;
  }
}

.milestone-badge {
  background-image: url("../../assets/team-lead/milestone-bg.svg");
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
  margin: 20px 0 30px;

  @size: 200px;
  width: @size;
  height: @size;

  img {
    @size: 50px;
    filter: brightness(200%);
    width: @size;
    height: @size;
    margin-top: 20px;
    margin-bottom: 5px;
  }

  h4,
  p {
    color: #ffffff;
    font-weight: bold;
  }

  h4 {
    font-size: 50px;
  }

  p {
    margin-top: 25px;
  }
}

@media (max-width: @screen-xs-max) {
  .team-lead-grid {
    max-width: 100%;
    column-gap: 10px;
  }

  .grid-item.milestone {
    width: 100%;
  }
}
</style>
<style lang="less">
@import (reference) "../../styles/bootstrap/variables.less";

.level-title.active {
  color: #ffffff;
}

.header-text {
  h4 {
    margin-top: 5px;
  }
}

.level-content {
  ul {
    list-style-type: none;

    li {
      p {
        color: @form-text-color;
      }
    }
  }
}

.milestone {
  h2,
  h3,
  h4 {
    color: @form-text-color;
    text-align: center;
  }
}

.completed.milestone {
  h2,
  h3,
  h4 {
    color: #ffffff;
  }
}
</style>
